/**
 * Subscriptions Admin Tab - טאב מנויים (ניהול)
 * Admin-only: subscription revenue report, active-subscribers list, editable plan
 * prices, and discount-coupon management.
 */

import { useEffect, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { RefreshCw, Check, TrendingUp, CreditCard, Users, Ticket, Plus, Trash2, Power, Pencil, X } from 'lucide-react'
import type { Coupon, CouponInput, CouponAppliesTo, CouponDiscountType } from '../../types'

function ils(n: number): string {
  return `${Math.round(n).toLocaleString('he-IL')} ₪`
}

function monthLabel(ym: string): string {
  // 'YYYY-MM' → 'MM/YYYY'
  const [y, m] = ym.split('-')
  return `${m}/${y}`
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const PLAN_LABELS: Record<string, string> = { annual: 'שנתי', monthly: 'חודשי' }
const APPLIES_LABELS: Record<CouponAppliesTo, string> = { annual: 'שנתי', monthly: 'חודשי', both: 'שנתי + חודשי', mazan: 'דוח איזון' }

function discountLabel(c: Coupon): string {
  return c.discountType === 'percentage' ? `${c.discountValue}%` : ils(c.discountValue)
}

export default function SubscriptionsAdminTab() {
  const {
    revenue,
    revenueLoading,
    revenueError,
    fetchRevenue,
    subscriptionPrices,
    pricesLoading,
    pricesError,
    fetchSubscriptionPrices,
    setSubscriptionPrices,
    subscribers,
    subscribersLoading,
    subscribersError,
    fetchSubscribers,
  } = useAdminStore()

  const [annualInput, setAnnualInput] = useState<string>('')
  const [monthlyInput, setMonthlyInput] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')

  useEffect(() => {
    fetchRevenue()
    fetchSubscriptionPrices()
    fetchSubscribers()
  }, [fetchRevenue, fetchSubscriptionPrices, fetchSubscribers])

  useEffect(() => {
    if (subscriptionPrices) {
      setAnnualInput(String(subscriptionPrices.annualIls))
      setMonthlyInput(String(subscriptionPrices.monthlyIls))
    }
  }, [subscriptionPrices])

  const annualNum = Number(annualInput)
  const monthlyNum = Number(monthlyInput)
  const validPrice = (n: number) => Number.isFinite(n) && n >= 0 && n <= 100000
  const pricesValid = validPrice(annualNum) && validPrice(monthlyNum)
  const pricesChanged =
    !!subscriptionPrices &&
    (annualNum !== subscriptionPrices.annualIls || monthlyNum !== subscriptionPrices.monthlyIls)

  const handleSavePrices = async () => {
    if (!pricesValid || !pricesChanged) return
    setSaving(true)
    setSuccessMsg('')
    try {
      await setSubscriptionPrices({ annualIls: annualNum, monthlyIls: monthlyNum })
      setSuccessMsg('המחירים עודכנו בהצלחה')
      setTimeout(() => setSuccessMsg(''), 3000)
    } catch {
      // error surfaced in store
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Revenue */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="text-primary-600" size={24} aria-hidden="true" />
            <h2 className="text-xl font-bold text-gray-900">הכנסות ממנויים</h2>
          </div>
          <button
            onClick={() => fetchRevenue()}
            disabled={revenueLoading}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <RefreshCw size={16} className={revenueLoading ? 'animate-spin' : ''} aria-hidden="true" />
            רענן
          </button>
        </div>

        {revenueError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" role="alert">{revenueError}</div>
        )}

        {/* Headline cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'התקבל החודש', bucket: revenue?.totals.month },
            { label: 'התקבל השנה', bucket: revenue?.totals.year },
            { label: 'סך הכול (מאז ומתמיד)', bucket: revenue?.totals.allTime },
          ].map((card) => (
            <div key={card.label} className="bg-primary-50/40 border border-primary-100 rounded-lg p-4">
              <p className="text-sm text-gray-600">{card.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{ils(card.bucket?.total ?? 0)}</p>
              <p className="text-xs text-gray-500 mt-1">{card.bucket?.count ?? 0} חיובים</p>
            </div>
          ))}
        </div>

        {/* Monthly breakdown */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-right font-medium text-gray-600">חודש</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">שנתי</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">חודשי</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">סך הכול</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">חיובים</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(revenue?.months ?? []).map((row) => (
                <tr key={row.month} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{monthLabel(row.month)}</td>
                  <td className="px-4 py-3 text-gray-700">{ils(row.annual)}</td>
                  <td className="px-4 py-3 text-gray-700">{ils(row.monthly)}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{ils(row.total)}</td>
                  <td className="px-4 py-3 text-gray-500">{row.count}</td>
                </tr>
              ))}
              {!revenueLoading && (revenue?.months?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    אין עדיין נתוני הכנסות.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          * הנתונים כוללים חיובים שנרשמו מרגע הפעלת הדיווח ואילך.
        </p>
      </div>

      {/* Active subscribers */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Users className="text-primary-600" size={24} aria-hidden="true" />
            <h2 className="text-xl font-bold text-gray-900">מנויים פעילים</h2>
          </div>
          <button
            onClick={() => fetchSubscribers()}
            disabled={subscribersLoading}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <RefreshCw size={16} className={subscribersLoading ? 'animate-spin' : ''} aria-hidden="true" />
            רענן
          </button>
        </div>

        {subscribersError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" role="alert">{subscribersError}</div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-right font-medium text-gray-600">שם</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">אימייל</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">מסלול</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">סכום</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">קופון</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">התחיל</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">חידוש / תוקף</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(subscribers ?? []).map((s) => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {`${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 ltr text-start">{s.email || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{s.planType ? PLAN_LABELS[s.planType] : '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{s.priceIls != null ? ils(s.priceIls) : '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{s.couponCode || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(s.startedAt)}</td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(s.currentPeriodEnd)}</td>
                </tr>
              ))}
              {!subscribersLoading && (subscribers?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    אין עדיין מנויים פעילים.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="text-primary-600" size={24} aria-hidden="true" />
          <h2 className="text-xl font-bold text-gray-900">מחירי מנוי</h2>
        </div>

        <p className="text-gray-600 text-sm mb-4">
          עדכון המחירים חל על <strong>מנויים חדשים בלבד</strong>. מנויים חודשיים קיימים ממשיכים
          להיות מחויבים במחיר שבו נרשמו.
        </p>

        {pricesError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" role="alert">{pricesError}</div>
        )}
        {successMsg && (
          <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-4 flex items-center gap-2">
            <Check size={16} aria-hidden="true" />
            {successMsg}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
          <div>
            <label htmlFor="price-annual" className="block text-sm font-medium text-gray-700 mb-1">מסלול שנתי (₪)</label>
            <input
              id="price-annual"
              type="number"
              dir="ltr"
              min={0}
              max={100000}
              step={1}
              value={annualInput}
              onChange={(e) => setAnnualInput(e.target.value)}
              disabled={pricesLoading || saving}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label htmlFor="price-monthly" className="block text-sm font-medium text-gray-700 mb-1">מסלול חודשי (₪)</label>
            <input
              id="price-monthly"
              type="number"
              dir="ltr"
              min={0}
              max={100000}
              step={1}
              value={monthlyInput}
              onChange={(e) => setMonthlyInput(e.target.value)}
              disabled={pricesLoading || saving}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        <button
          onClick={handleSavePrices}
          disabled={saving || !pricesValid || !pricesChanged}
          className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
        >
          {saving ? 'שומר...' : 'שמירת מחירים'}
        </button>
      </div>

      {/* Coupons */}
      <CouponSection />
    </div>
  )
}

// ──────────────────────────── Coupon management ────────────────────────────

const EMPTY_FORM: CouponInput = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: 0,
  appliesTo: 'both',
  validFrom: '',
  validUntil: '',
  active: true,
  maxRedemptions: null,
}

// 'YYYY-MM-DDTHH:mm:ss...' → 'YYYY-MM-DD' for <input type="date">
function toDateInput(iso?: string | null): string {
  if (!iso) return ''
  return new Date(iso).toISOString().slice(0, 10)
}

function CouponSection() {
  const {
    coupons,
    couponsLoading,
    couponsError,
    fetchCoupons,
    createCoupon,
    updateCoupon,
    toggleCoupon,
    deleteCoupon,
  } = useAdminStore()

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<CouponInput>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')

  useEffect(() => {
    fetchCoupons()
  }, [fetchCoupons])

  const openCreate = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormError('')
    setShowForm(true)
  }

  const openEdit = (c: Coupon) => {
    setEditingId(c._id)
    setForm({
      code: c.code,
      description: c.description ?? '',
      discountType: c.discountType,
      discountValue: c.discountValue,
      appliesTo: c.appliesTo,
      validFrom: toDateInput(c.validFrom),
      validUntil: toDateInput(c.validUntil),
      active: c.active,
      maxRedemptions: c.maxRedemptions ?? null,
    })
    setFormError('')
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingId(null)
  }

  const handleSubmit = async () => {
    setFormError('')
    if (!form.code.trim()) {
      setFormError('יש להזין קוד קופון')
      return
    }
    if (!Number.isFinite(form.discountValue) || form.discountValue <= 0) {
      setFormError('יש להזין ערך הנחה חיובי')
      return
    }
    if (form.discountType === 'percentage' && form.discountValue > 100) {
      setFormError('אחוז הנחה חייב להיות עד 100')
      return
    }

    // Build a clean payload — empty dates → null, empty maxRedemptions → null.
    const payload: CouponInput = {
      code: form.code.trim().toUpperCase(),
      description: form.description?.trim() || undefined,
      discountType: form.discountType,
      discountValue: form.discountValue,
      appliesTo: form.appliesTo,
      validFrom: form.validFrom ? form.validFrom : null,
      validUntil: form.validUntil ? form.validUntil : null,
      active: form.active,
      maxRedemptions: form.maxRedemptions ?? null,
    }

    setSubmitting(true)
    try {
      if (editingId) {
        await updateCoupon(editingId, payload)
      } else {
        await createCoupon(payload)
      }
      closeForm()
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'שגיאה בשמירת הקופון')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (c: Coupon) => {
    if (!window.confirm(`למחוק את הקופון ${c.code}?`)) return
    try {
      await deleteCoupon(c._id)
    } catch {
      // error surfaced in store
    }
  }

  const inputCls =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Ticket className="text-primary-600" size={24} aria-hidden="true" />
          <h2 className="text-xl font-bold text-gray-900">קודי קופון</h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchCoupons()}
            disabled={couponsLoading}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <RefreshCw size={16} className={couponsLoading ? 'animate-spin' : ''} aria-hidden="true" />
            רענן
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium transition-colors"
          >
            <Plus size={16} aria-hidden="true" />
            קופון חדש
          </button>
        </div>
      </div>

      <p className="text-gray-600 text-sm mb-4">
        קוד הנחה שניתן להחיל ברכישת מנוי. שינוי או ביטול קופון <strong>אינו משפיע</strong> על
        מנויים חודשיים קיימים — הם ממשיכים במחיר שבו נרשמו.
      </p>

      {couponsError && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" role="alert">{couponsError}</div>
      )}

      {/* Create / edit form */}
      {showForm && (
        <div className="border border-primary-200 bg-primary-50/30 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">{editingId ? 'עריכת קופון' : 'קופון חדש'}</h3>
            <button onClick={closeForm} className="text-gray-400 hover:text-gray-600" aria-label="סגירה">
              <X size={18} aria-hidden="true" />
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 text-red-700 p-2 rounded text-sm mb-3" role="alert">{formError}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="coupon-code" className="block text-sm font-medium text-gray-700 mb-1">קוד קופון</label>
              <input
                id="coupon-code"
                type="text"
                dir="ltr"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="IFTACH70"
                className={`${inputCls} ltr text-start`}
              />
            </div>
            <div>
              <label htmlFor="coupon-description" className="block text-sm font-medium text-gray-700 mb-1">תיאור (אופציונלי)</label>
              <input
                id="coupon-description"
                type="text"
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="coupon-discount-type" className="block text-sm font-medium text-gray-700 mb-1">סוג הנחה</label>
              <select
                id="coupon-discount-type"
                value={form.discountType}
                onChange={(e) => setForm({ ...form, discountType: e.target.value as CouponDiscountType })}
                className={inputCls}
              >
                <option value="percentage">אחוז (%)</option>
                <option value="fixed">סכום קבוע (₪)</option>
              </select>
            </div>
            <div>
              <label htmlFor="coupon-discount-value" className="block text-sm font-medium text-gray-700 mb-1">
                ערך ההנחה {form.discountType === 'percentage' ? '(%)' : '(₪)'}
              </label>
              <input
                id="coupon-discount-value"
                type="number"
                dir="ltr"
                min={0}
                max={form.discountType === 'percentage' ? 100 : 100000}
                step={1}
                value={String(form.discountValue)}
                onChange={(e) => setForm({ ...form, discountValue: Number(e.target.value) })}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="coupon-applies-to" className="block text-sm font-medium text-gray-700 mb-1">חל על</label>
              <select
                id="coupon-applies-to"
                value={form.appliesTo}
                onChange={(e) => setForm({ ...form, appliesTo: e.target.value as CouponAppliesTo })}
                className={inputCls}
              >
                <option value="both">שנתי + חודשי</option>
                <option value="annual">שנתי בלבד</option>
                <option value="monthly">חודשי בלבד</option>
                <option value="mazan">דוח איזון (חד־פעמי)</option>
              </select>
            </div>
            <div>
              <label htmlFor="coupon-max-redemptions" className="block text-sm font-medium text-gray-700 mb-1">
                מגבלת שימושים (אופציונלי)
              </label>
              <input
                id="coupon-max-redemptions"
                type="number"
                dir="ltr"
                min={1}
                step={1}
                value={form.maxRedemptions ?? ''}
                onChange={(e) =>
                  setForm({ ...form, maxRedemptions: e.target.value ? Number(e.target.value) : null })
                }
                placeholder="ללא הגבלה"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="coupon-valid-from" className="block text-sm font-medium text-gray-700 mb-1">בתוקף מ־ (אופציונלי)</label>
              <input
                id="coupon-valid-from"
                type="date"
                value={form.validFrom ?? ''}
                onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="coupon-valid-until" className="block text-sm font-medium text-gray-700 mb-1">בתוקף עד (אופציונלי)</label>
              <input
                id="coupon-valid-until"
                type="date"
                value={form.validUntil ?? ''}
                onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 mt-4 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.active ?? true}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            קופון פעיל
          </label>

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium transition-colors"
            >
              {submitting ? 'שומר...' : editingId ? 'עדכון קופון' : 'יצירת קופון'}
            </button>
            <button
              onClick={closeForm}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
            >
              ביטול
            </button>
          </div>
        </div>
      )}

      {/* Coupons table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-right font-medium text-gray-600">קוד</th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">הנחה</th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">חל על</th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">תוקף</th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">שימושים</th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">סטטוס</th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">פעולות</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(coupons ?? []).map((c) => (
              <tr key={c._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 font-semibold text-gray-900 ltr text-start">{c.code}</td>
                <td className="px-4 py-3 text-gray-700">{discountLabel(c)}</td>
                <td className="px-4 py-3 text-gray-700">{APPLIES_LABELS[c.appliesTo]}</td>
                <td className="px-4 py-3 text-gray-500">
                  {c.validFrom || c.validUntil
                    ? `${formatDate(c.validFrom)} – ${formatDate(c.validUntil)}`
                    : 'ללא הגבלה'}
                </td>
                <td className="px-4 py-3 text-gray-500">
                  {c.redemptionCount}
                  {c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ''}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {c.active ? 'פעיל' : 'כבוי'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleCoupon(c._id, !c.active)}
                      title={c.active ? 'כיבוי' : 'הפעלה'}
                      aria-label={c.active ? 'כיבוי' : 'הפעלה'}
                      className="text-gray-400 hover:text-primary-600 transition-colors"
                    >
                      <Power size={16} aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => openEdit(c)}
                      title="עריכה"
                      aria-label="עריכה"
                      className="text-gray-400 hover:text-primary-600 transition-colors"
                    >
                      <Pencil size={16} aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => handleDelete(c)}
                      title="מחיקה"
                      aria-label="מחיקה"
                      className="text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!couponsLoading && (coupons?.length ?? 0) === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  אין עדיין קופונים. צרו קופון חדש כדי להתחיל.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
